import { ArrowLeft } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { useLandingHeroImagesQuery } from '@/entities/landing-image';
import { HOME_PATH } from '@/shared/config';
import { AuthBrandPanel } from './auth-brand-panel';

interface AuthShellProps {
  /** 화면 제목(h1) — 한글 그대로 크게 보인다. */
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
  /** 본문 아래에 놓는 보조 안내(다른 인증 화면으로 가는 링크 등). */
  readonly footer?: ReactNode;
}

/**
 * 로그인·회원가입 등 인증 화면 틀 — 왼쪽 흑연 브랜드 판과 오른쪽 폼 칸으로 나눈다.
 * 좁은 화면에서는 브랜드 판이 로고 띠로 줄고 폼이 그 아래로 흐른다. 홈으로 가는 길은 로고와 오른쪽 위 링크 두 곳이다.
 */
export const AuthShell = ({
  title,
  description,
  children,
  footer,
}: AuthShellProps): ReactElement => {
  const banners = useLandingHeroImagesQuery();
  const imageUrl = banners.data?.[0]?.imageUrl ?? null;

  return (
    <div className="flex min-h-dvh flex-col bg-background break-keep lg:flex-row">
      <AuthBrandPanel imageUrl={imageUrl} />

      <main className="flex flex-1 flex-col px-5 sm:px-8 lg:px-14">
        <div className="hidden justify-end pt-10 lg:flex">
          <Link
            to={HOME_PATH}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft aria-hidden strokeWidth={1.5} className="size-4" />
            홈으로
          </Link>
        </div>

        <div className="mx-auto flex w-full max-w-[400px] flex-1 flex-col justify-center pt-10 pb-16 sm:pt-14 lg:py-16">
          <header className="mb-10 flex flex-col gap-3">
            <h1 className="text-[2rem] leading-[1.2] font-bold tracking-[-0.03em] sm:text-[2.25rem]">
              {title}
            </h1>
            {description ? (
              <p className="text-[15px] leading-relaxed text-muted-foreground">{description}</p>
            ) : null}
          </header>

          {children}

          {footer ? <div className="mt-10 text-sm">{footer}</div> : null}
        </div>
      </main>
    </div>
  );
};
